/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {SensorTypes} from '@ion/core/src/types/entities';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {BackButton} from '@ion/dls/src/components/buttons/BackButton';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';

export interface SensorBreadcrumbsProps {
  label: string;
  selectedSensor: SensorTypes;
  onBackClick?: MouseEventHandler<HTMLDivElement>;
}

const useStyles = makeStyles((theme) => ({
  nameLabel : {
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    maxWidth     : '125px',
    padding      : '0px !important',
    color        : dlsColor('grey3'),
    ...dlsAlt('typography', 'cta2')
  },
  accordionHeader : {
    ...dlsTypography('panelCards'),
    fontFamily  : 'Roboto',
    fontWeight  : '600',
    fontSize    : '14px',
    lineHeight  : '17px',
    color       : dlsColor('black'),
    minHeight   : 48,
    paddingLeft : 15
  },
  breadcrumbs : {
    padding : 0
  },
  sensorBreadcrumb : {
    cursor         : 'pointer',
    textDecoration : 'none !important',
    color          : `${dlsColor('grey3')}`
  },
  sensorLabel : {
    textDecoration : 'none',
    pointerEvents  : 'none',
    textTransform  : 'uppercase',
    fontWeight     : 700,
    color          : `${dlsColor('grey1')}`
  },
  // the current theme's breadcrumb uses the Asset Details Drawer's section
  // title style (AccordionSectionHeader) for both of its crumbs ...
  currentBreadcrumbs : {
    boxSizing  : 'border-box',
    display    : 'flex',
    alignItems : 'center',
    minWidth   : 0,
    padding    : '8px 16px 8px 0',
    gap        : '8px'
  },
  currentCrumb : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '16px',
    lineHeight    : '18px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    whiteSpace    : 'nowrap',
    color         : dlsColor('grey2')
  },
  currentParentCrumb : {
    cursor       : 'pointer',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    color        : dlsColor('grey3')
  }
}));

/**
 * Defines the SensorBreadcrumbs component for the Sensor Summary accordion.
 *
 * ```typescript
 *
 * import SensorBreadcrumbs from './SensorBreadcrumbs';
 *
 * <SensorBreadcrumbs label="Sensor Name" onBackClick={handleBackClick} />
 *
 * ```
 */
const SensorBreadcrumbs = ({
  label,
  selectedSensor,
  onBackClick
}: SensorBreadcrumbsProps) => {
  const classes     = useStyles();
  const {isCurrent} = useThemeContext();

  let result;
  if (!isNil(selectedSensor) && isCurrent) {
    result = (
      <div className={classes.currentBreadcrumbs} onClick={onBackClick}>
        {/* the current theme's back control, as in the Asset Details
          * Drawer header - its click bubbles to this breadcrumb's own back
          * handler ... */}
        <BackButton aria-label={translate('common:editors.shared.back')} />
        <span className={classnames(classes.currentCrumb, classes.currentParentCrumb)}>
          {translate('common:smarterTrailer.title')}
        </span>
        <span className={classes.currentCrumb}>
          /
        </span>
        <span className={classes.currentCrumb}>
          {label}
        </span>
      </div>
    );
  } else if (!isNil(selectedSensor)) {
    result = (
      <div className={classes.breadcrumbs} onClick={onBackClick}>
        <Breadcrumb icon={<NavigationIcon direction='back' />} height={'10px'}>
          <span className={classnames(classes.sensorBreadcrumb, classes.nameLabel)} onClick={onBackClick}>
            {translate('common:smarterTrailer.title')}
          </span>
          <span className={classes.sensorLabel}>
            {label}
          </span>
        </Breadcrumb>
      </div>
    );
  } else {
    result = (
      <span className={classes.accordionHeader}>
        {translate('common:smarterTrailer.title')}
      </span>
    );
  }

  return result;
};

export default SensorBreadcrumbs;
